// Expert Status Widget for AgendaPage
// Compact widget that expands when status needs attention

import React, { useEffect, useState } from 'react';
import { AlertCircle, Calendar, TrendingUp, X } from 'lucide-react';
import { getExpertStatus } from '../../services/expertStatusService';
import { ExpertStatus } from '../../types/expertStatus.types';
import { MotionCard } from '../ui/motion-card';

interface ExpertStatusWidgetProps {
  userId: string;
  onPlanConversation: () => void;
}

const ExpertStatusWidget: React.FC<ExpertStatusWidgetProps> = ({ 
  userId, 
  onPlanConversation 
}) => {
  const [status, setStatus] = useState<ExpertStatus | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    loadStatus();
  }, [userId]);

  const loadStatus = async () => {
    try {
      const data = await getExpertStatus(userId);
      setStatus(data);
      
      // Auto-expand if needs attention
      if (data && (data.healthStatus === 'urgent' || data.healthStatus === 'expiring')) {
        setExpanded(true);
      }
    } catch (error) {
      console.error('Error loading expert status:', error);
    }
  };

  if (!status || status.level === 'none' || dismissed) return null;

  const needsAttention = status.healthStatus === 'urgent' || status.healthStatus === 'expiring';
  
  if (!needsAttention && !expanded) {
    // Compact mode - only show if healthy
    return (
      <MotionCard 
        onClick={() => setExpanded(true)}
        className="bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200 p-3 cursor-pointer"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className={`w-2 h-2 rounded-full ${status.health >= 70 ? 'bg-emerald-500' : 'bg-yellow-500'}`}></div>
            <span className="text-sm font-medium text-gray-700">
              Expert Status: {status.health}%
            </span>
          </div>
          <TrendingUp size={16} className="text-gray-400" />
        </div>
      </MotionCard>
    );
  }

  // Expanded mode
  return (
    <MotionCard className={`p-4 ${
      needsAttention 
        ? 'bg-orange-50 border-orange-300' 
        : 'bg-blue-50 border-blue-200'
    }`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-2">
          {needsAttention && <AlertCircle size={20} className="text-orange-500" />}
          <h4 className="font-semibold text-gray-800">Expert Status Update</h4>
        </div>
        <button
          onClick={() => {
            setExpanded(false);
            if (!needsAttention) setDismissed(true);
          }}
          className="p-1 hover:bg-white/50 rounded"
        >
          <X size={16} className="text-gray-500" />
        </button>
      </div>

      {/* Status Info */}
      <div className="mb-3">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-gray-600">Health</span>
          <span className={`text-sm font-semibold ${
            status.health >= 70 ? 'text-emerald-600' : 
            status.health >= 50 ? 'text-yellow-600' : 
            'text-orange-600'
          }`}>
            {status.health}%
          </span>
        </div>
        <div className="w-full bg-white rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all ${
              status.health >= 70 ? 'bg-emerald-500' : 
              status.health >= 50 ? 'bg-yellow-500' : 
              'bg-orange-500'
            }`}
            style={{ width: `${status.health}%` }}
          ></div>
        </div>
      </div>

      {/* Action Required */}
      {needsAttention && (
        <div className="mb-3 p-3 bg-white rounded-lg">
          <p className="text-sm font-medium text-gray-800 mb-2">
            🎯 Actie vereist
          </p>
          <ul className="text-xs text-gray-600 space-y-1">
            {status.conversationsLast30Days < 1 && (
              <li>• Plan minimaal 1 gesprek deze maand</li>
            )}
            {status.daysUntilDowngrade !== null && status.daysUntilDowngrade <= 3 && (
              <li>• Log in binnen {status.daysUntilDowngrade} dag{status.daysUntilDowngrade !== 1 ? 'en' : ''}</li>
            )}
          </ul>
        </div>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-3 gap-2 mb-3 text-center">
        <div>
          <p className="text-lg font-bold text-gray-800">{status.conversationsLast30Days}</p>
          <p className="text-xs text-gray-600">Gesprekken</p>
        </div>
        <div>
          <p className="text-lg font-bold text-gray-800">{status.loginStreak}</p>
          <p className="text-xs text-gray-600">Streak</p>
        </div>
        <div>
          <p className="text-lg font-bold text-gray-800">
            {status.daysUntilDowngrade ?? '∞'}
          </p>
          <p className="text-xs text-gray-600">Dagen over</p>
        </div>
      </div>

      {/* CTA */}
      <button
        onClick={onPlanConversation}
        className="w-full py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-medium text-sm flex items-center justify-center space-x-2 transition-colors"
      >
        <Calendar size={16} />
        <span>Plan een gesprek</span>
      </button>
    </MotionCard>
  );
};

export default ExpertStatusWidget;
